import type { DirectUploaderInterface, ProgressListener } from './types'

import { SfLog } from '@app/adapters/logger'
import {
  DocumentLinkType,
  DirectUploadDocument,
  RawExistingDocument,
  RawExistingPermanentDocument,
  ExistingDocument,
  ExistingPermanentDocument,
} from '@app/shared/types/sf-data'
import { FileWithProgress } from '@app/shared/vue/components/direct-upload/types'
import { DirectUpload, Blob as ActiveBlob } from '@rails/activestorage'

export class DirectUploader implements DirectUploaderInterface {
  file: File
  progressListener: ProgressListener
  private _upload: DirectUpload

  constructor(file: File, progressListener: ProgressListener) {
    this.file = file
    this.progressListener = progressListener
    this._upload = new DirectUpload(this.file, this.getUrl(), this)
  }

  upload(): Promise<ActiveBlob> {
    return new Promise((resolve, reject) => {
      this._upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          if (blob.byte_size === 0) {
            SfLog.warn('Testing capturing for byte size 0', {
              extra: {
                file: this.file,
                uploadUrl: this.getUrl(),
                blob: blob,
              },
            })
          }
          resolve(blob)
        }
      })
    })
  }

  directUploadWillStoreFileWithXHR(request: XMLHttpRequest): void {
    request.upload.addEventListener('progress', (event) =>
      this.directUploadDidProgress(event)
    )
  }

  directUploadDidProgress(
    event: ProgressEvent<XMLHttpRequestEventTarget>
  ): void {
    const progress = ((event.loaded / event.total) * 100).toFixed(0)

    this.progressListener(progress)
  }

  getUrl(): string {
    const firmId = window.Silverfin.currentFirmId
    const companyId = window.Silverfin.currentCompanyId
    const userId = window.Silverfin.currentUserId()

    return window.Silverfin.Routes.rails_direct_uploads_path({
      firm_id: firmId,
      company_id: companyId,
      user_id: userId,
    })
  }
}

export function formatUploadedFile(
  uuid: string,
  file: FileWithProgress
): DirectUploadDocument {
  return {
    linkType: DocumentLinkType.DirectUpload,
    link: file?.blob?.signed_id || '',
    name: file?.blob?.filename || '',
    contentType: file?.blob?.content_type || '',
    size: file?.blob?.byte_size || 0,
    unique_id: `upload-${uuid}`,
  }
}

export function formatExistingDocument(
  file: RawExistingDocument
): ExistingDocument {
  return {
    document_id: file.id,
    linkType: DocumentLinkType.Document,
    name: file.title,
    unique_id: `document-${file.id}`,
  }
}

export function formatExistingPermanentDocument(
  file: RawExistingPermanentDocument
): ExistingPermanentDocument {
  return {
    document_id: file.documentId,
    linkType: DocumentLinkType.PermanentDocument,
    name: file.title,
    permanent_document_id: file.id,
    unique_id: `permanent-${file.id}`,
  }
}
